import {
  computePolygonArea,
  isPolygon2D,
} from '@shared/helpers/geometry/geometry.helpers';
import type { Point2D, Polygon2D } from '@shared/models/geometry.model';
import type { DraftShape } from '@shared/models/design/storey.model';
import { createDraftShape } from '@shared/helpers/design/factories';
import {
  generateHShape,
  generateLShape,
  generateRectangle,
  generateTShape,
  generateUShape,
} from '@shared/helpers/design/shape-templates.helpers';
import { rotatePoint2DClockwise } from '@shared/helpers/design/blueprint-underlay-geometry.helpers';

export type BuildingFootprintTemplateId =
  | 'custom'
  | 'square'
  | 'bar'
  | 'rectangle'
  | 'l-shape'
  | 'u-shape'
  | 't-shape'
  | 'h-shape'
  | 'c-shape'
  | 'cross-shape'
  | 'z-shape'
  | 'e-shape'
  | 'square-courtyard'
  | 'double-courtyard';

export type BuildingFootprintTemplate = {
  id: BuildingFootprintTemplateId;
  label: string;
  points?: Polygon2D;
  cutouts?: Polygon2D[];
};

export type BuildingFootprintTemplateGeometry = {
  points: Polygon2D;
  cutouts: Polygon2D[];
};

export const DEFAULT_BUILDING_FOOTPRINT_TEMPLATE: BuildingFootprintTemplateId =
  'custom';

const C_SHAPE: Polygon2D = [
  [-12, -9],
  [12, -9],
  [12, -4],
  [-6, -4],
  [-6, 4],
  [12, 4],
  [12, 9],
  [-12, 9],
];

const CROSS_SHAPE: Polygon2D = [
  [-4, -11],
  [4, -11],
  [4, -4],
  [11, -4],
  [11, 4],
  [4, 4],
  [4, 11],
  [-4, 11],
  [-4, 4],
  [-11, 4],
  [-11, -4],
  [-4, -4],
];

const Z_SHAPE: Polygon2D = [
  [-12, -9],
  [4, -9],
  [4, -3],
  [12, -3],
  [12, 9],
  [-4, 9],
  [-4, 3],
  [-12, 3],
];

const E_SHAPE: Polygon2D = [
  [-12, -10],
  [12, -10],
  [12, -6],
  [-5, -6],
  [-5, -2],
  [7, -2],
  [7, 2],
  [-5, 2],
  [-5, 6],
  [12, 6],
  [12, 10],
  [-12, 10],
];

const SQUARE_COURTYARD = generateRectangle(24, 24);
const SQUARE_COURTYARD_CUTOUT = generateRectangle(12, 12);
const DOUBLE_COURTYARD = generateRectangle(36, 20);
const DOUBLE_COURTYARD_CUTOUTS: Polygon2D[] = [
  [
    [-13, -4],
    [-3, -4],
    [-3, 4],
    [-13, 4],
  ],
  [
    [3, -4],
    [13, -4],
    [13, 4],
    [3, 4],
  ],
];

export const BUILDING_FOOTPRINT_TEMPLATES: readonly BuildingFootprintTemplate[] =
  [
    { id: 'custom', label: 'Custom' },
    {
      id: 'square',
      label: 'Square',
      points: generateRectangle(16, 16),
    },
    {
      id: 'bar',
      label: 'Bar',
      points: generateRectangle(28, 8),
    },
    {
      id: 'rectangle',
      label: 'Rectangle',
      points: generateRectangle(20, 12),
    },
    {
      id: 'l-shape',
      label: 'L-shape',
      points: generateLShape(20, 16, 8, 8),
    },
    {
      id: 'u-shape',
      label: 'U-shape',
      points: generateUShape(24, 16, 10, 10),
    },
    {
      id: 't-shape',
      label: 'T-shape',
      points: generateTShape(22, 18, 8, 6),
    },
    {
      id: 'h-shape',
      label: 'H-shape',
      points: generateHShape(24, 18, 6, 6),
    },
    {
      id: 'c-shape',
      label: 'C-shape',
      points: C_SHAPE,
    },
    {
      id: 'cross-shape',
      label: 'Cross',
      points: CROSS_SHAPE,
    },
    {
      id: 'z-shape',
      label: 'Z-shape',
      points: Z_SHAPE,
    },
    {
      id: 'e-shape',
      label: 'E-shape',
      points: E_SHAPE,
    },
    {
      id: 'square-courtyard',
      label: 'Square courtyard',
      points: SQUARE_COURTYARD,
      cutouts: [SQUARE_COURTYARD_CUTOUT],
    },
    {
      id: 'double-courtyard',
      label: 'Double courtyard',
      points: DOUBLE_COURTYARD,
      cutouts: DOUBLE_COURTYARD_CUTOUTS,
    },
  ];

export const FOOTPRINT_TEMPLATE_DRAG_TYPE =
  'application/x-nodon-footprint-template';

export const getBuildingFootprintTemplate = (
  id: BuildingFootprintTemplateId,
): BuildingFootprintTemplate =>
  BUILDING_FOOTPRINT_TEMPLATES.find((template) => template.id === id) ??
  BUILDING_FOOTPRINT_TEMPLATES[0]!;

/** Size the preset to GFA / storeys before previewing or placing it. */
export const getBuildingFootprintTemplateGeometry = (
  id: BuildingFootprintTemplateId,
  sizing?: { targetGfa?: number; storeyCount: number },
): BuildingFootprintTemplateGeometry | undefined => {
  const template = getBuildingFootprintTemplate(id);
  if (!template.points) return undefined;
  const geometry = { points: template.points, cutouts: template.cutouts ?? [] };
  if (
    !sizing?.targetGfa ||
    !Number.isFinite(sizing.targetGfa) ||
    sizing.targetGfa <= 0 ||
    !Number.isSafeInteger(sizing.storeyCount) ||
    sizing.storeyCount < 1
  )
    return geometry;
  const area =
    computePolygonArea(geometry.points) -
    geometry.cutouts.reduce(
      (sum, cutout) => sum + computePolygonArea(cutout),
      0,
    );
  if (area <= 0) return geometry;
  const scale = Math.sqrt(sizing.targetGfa / sizing.storeyCount / area);
  const scaleRing = (ring: Polygon2D): Polygon2D => {
    const scaled = ring.map(([x, y]): Point2D => [x * scale, y * scale]);
    if (!isPolygon2D(scaled)) throw new Error('Invalid scaled footprint');
    return scaled;
  };
  return {
    points: scaleRing(geometry.points),
    cutouts: geometry.cutouts.map(scaleRing),
  };
};

/**
 * Keep a preset's screen orientation equal to its panel preview.
 * The panel flips the geometry y-axis, then the map bearing is cancelled in
 * local screen-oriented coordinates.
 */
export const orientBuildingFootprintTemplateToScreen = (
  points: Polygon2D,
  bearingDegrees: number,
  rotationDegrees = 0,
): Polygon2D =>
  points.map(([x, y]) =>
    rotatePoint2DClockwise([x, -y], bearingDegrees + rotationDegrees),
  ) as Polygon2D;

/** Resolve pointer-driven template rotation relative to its screen orientation. */
export const getBuildingFootprintTemplateRotation = (
  center: Point2D,
  pointer: Point2D,
  bearingDegrees: number,
  snapToFiveDegrees = false,
): number => {
  const dx = pointer[0] - center[0];
  const dy = pointer[1] - center[1];
  if (dx === 0 && dy === 0) return 0;
  const screenRelativeDegrees =
    (Math.atan2(dy, dx) * 180) / Math.PI - bearingDegrees;
  return snapToFiveDegrees
    ? Math.round(screenRelativeDegrees / 5) * 5
    : screenRelativeDegrees;
};

/** Center a footprint template on the current local map pointer. */
export const placeBuildingFootprintTemplate = (
  points: Polygon2D,
  center: Point2D,
): Polygon2D => {
  const translate = ([x, y]: Point2D): Point2D => [
    x + center[0],
    y + center[1],
  ];
  const [first, second, third, ...rest] = points;
  return [
    translate(first),
    translate(second),
    translate(third),
    ...rest.map(translate),
  ];
};

/** Build the complete draft committed by a single preset-placement click. */
export const createBuildingFootprintTemplateDraft = (
  points: Polygon2D,
  center: Point2D,
  bearingDegrees = 0,
  rotationDegrees = 0,
): DraftShape =>
  createDraftShape({
    points: placeBuildingFootprintTemplate(
      orientBuildingFootprintTemplateToScreen(
        points,
        bearingDegrees,
        rotationDegrees,
      ),
      center,
    ),
  });

/** Build the solid and any courtyard cutouts for one preset placement. */
export const createBuildingFootprintTemplateDrafts = (
  template: BuildingFootprintTemplateGeometry,
  center: Point2D,
  bearingDegrees = 0,
  rotationDegrees = 0,
): DraftShape[] => [
  createBuildingFootprintTemplateDraft(
    template.points,
    center,
    bearingDegrees,
    rotationDegrees,
  ),
  ...template.cutouts.map((points) => ({
    ...createBuildingFootprintTemplateDraft(
      points,
      center,
      bearingDegrees,
      rotationDegrees,
    ),
    cutout: true,
  })),
];
